import { DESIGNER_EXPERIENCE_LEVELS } from "@/libs/platform/creative-profile";
import {
	Alert,
	Button,
	Checkbox,
	Divider,
	Grid,
	Group,
	PasswordInput,
	Select,
	Text,
	TextInput,
} from "@mantine/core";
import { Form } from "@remix-run/react";
import { IconAlertCircle } from "@tabler/icons-react";
import { forwardRef, useState } from "react";
import { PrivacyTermsDialog } from "./privacy-terms-dialog";

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
	value: string;
	label: string;
	description: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
	({ label, description, ...others }: ItemProps, ref) => (
		<div ref={ref} {...others}>
			<Group noWrap>
				<div>
					<Text italic color="inherit">
						{label}
					</Text>
					<Text size="xs" opacity={0.65} color="inherit">
						{description}
					</Text>
				</div>
			</Group>
		</div>
	),
);

SelectItem.displayName = "SelectItem";

type Properties = {
	errors?: {
		firstName?: string[];
		lastName?: string[];
		email?: string[];
		password?: string[];
		passwordConfirmation?: string[];
		level?: string[];
		privacyPolicy?: string[];
	};
	formErrors?: string[];
	loading?: boolean;
};

export const DesignerRegistrationForm = ({
	errors,
	formErrors,
	loading,
}: Properties) => {
	const [policyOpen, setPolicyOpen] = useState<boolean>(false);
	const [policyChecked, setPolicyChecked] = useState(false);

	return (
		<Form method="post">
			<Grid mt={36}>
				<Grid.Col md={6}>
					<TextInput
						name="firstName"
						label="First name"
						placeholder="First name"
						error={errors?.firstName?.[0]}
					/>
				</Grid.Col>
				<Grid.Col md={6}>
					<TextInput
						name="lastName"
						label="Last name"
						placeholder="Last name"
						error={errors?.lastName?.[0]}
					/>
				</Grid.Col>
				<Grid.Col>
					<TextInput
						type="email"
						name="email"
						label="Email address"
						placeholder="Email address"
						error={errors?.email?.[0]}
					/>
				</Grid.Col>
				<Grid.Col>
					<PasswordInput
						name="password"
						label="Create password"
						placeholder="Create password"
						error={errors?.password?.[0]}
					/>
				</Grid.Col>
				<Grid.Col>
					<PasswordInput
						name="passwordConfirmation"
						label="Confirm password"
						placeholder="Confirm password"
						error={errors?.passwordConfirmation?.[0]}
					/>
				</Grid.Col>
				<Grid.Col>
					<Select
						name="level"
						label="Choose your experience level"
						placeholder="Experience level"
						error={errors?.level?.[0]}
						data={DESIGNER_EXPERIENCE_LEVELS}
						itemComponent={SelectItem}
						maxDropdownHeight={400}
					/>
				</Grid.Col>
			</Grid>
			<Divider mt="xl" mb="sm" />
			<Checkbox
				name="privacyPolicy"
				onClick={(e) => {
					e.preventDefault();
					setPolicyOpen(true);
				}}
				checked={policyChecked}
				onChange={() => {}}
				error={errors?.privacyPolicy?.[0]}
				mt="lg"
				label={
					<>
						I have read and acknowledged the{" "}
						<Text component="span" weight={600}>
							Privacy Policy
						</Text>{" "}
						and the{" "}
						<Text weight={600} component="span">
							Terms and conditions
						</Text>
					</>
				}
			/>
			{formErrors?.length ? (
				<Alert color="red" mt="md" icon={<IconAlertCircle size={16} />}>
					{formErrors[0]}
				</Alert>
			) : null}
			<Button loading={loading} fullWidth type="submit" color="green" mt="xl">
				Create account
			</Button>
			{policyOpen && (
				<PrivacyTermsDialog
					onClose={() => setPolicyOpen(false)}
					onAccept={() => {
						setPolicyOpen(false);
						setPolicyChecked(true);
					}}
					readOnly={policyChecked}
				/>
			)}
		</Form>
	);
};
